import type { WorkflowType } from "@entrilia/shared/api/workflowTypes";
import type { StateAction } from "@entrilia/shared/types";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { MatchDocumentsDecision } from "../../../../api/types/workflowTypes";
import type { ClassifiedFiles } from "./documentWorkflowService";
import { classifyNewFiles } from "./documentWorkflowService";

export type AnalysisStatus = "Pending" | "Analyzing" | "Analyzed" | "Failed";

export type FileStatus =
  "Uploading" | "UploadFailed" | "Uploaded" | "Duplicate" | "Queueing" | "QueueFailed" | "Queued" | "WorkflowStarted";

export interface FileEntry {
  id: string;
  file: File;
  status: FileStatus;
  catalogueId?: string;
  error?: string;
  runError?: string;
  sourceId?: string;
  workflowId?: string;
  workflowTemplateId?: string;
  workflowDestination?: string;
  analysisStatus?: AnalysisStatus;
  matchDecision?: MatchDocumentsDecision;
  analysisError?: string;
  selectedWorkflowType?: WorkflowType;
}

export interface UploadDocumentsState {
  files: FileEntry[];
}

const canBeQueued = (status: FileStatus): boolean => status === "Uploaded" || status === "QueueFailed";

export const createFileEntry = (file: File): FileEntry => ({
  id: generateGuid(),
  file,
  status: "Uploading",
});

export const classifyFiles = (newFiles: File[], existingEntries: FileEntry[]): ClassifiedFiles<FileEntry> =>
  classifyNewFiles(newFiles, existingEntries, createFileEntry, (file) => ({
    ...createFileEntry(file),
    status: "Duplicate",
  }));

export const addFilesAction =
  (entries: FileEntry[]): StateAction<UploadDocumentsState> =>
  (state) => ({ ...state, files: [...state.files, ...entries] });

export const setStatusAction =
  (id: string, status: FileStatus, error?: string): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id) {
        return file;
      }

      const allowedFrom: FileStatus[] = ["Uploading", "UploadFailed"];
      if (!allowedFrom.includes(file.status)) {
        return file;
      }

      return {
        ...file,
        status,
        error,
        runError: undefined,
        sourceId: undefined,
        workflowId: undefined,
        workflowTemplateId: undefined,
        workflowDestination: undefined,
      };
    }),
  });

export const setCatalogueIdAction =
  (id: string, catalogueId: string): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id || file.status !== "Uploading") {
        return file;
      }

      return {
        ...file,
        catalogueId,
        status: "Uploaded",
        analysisStatus: "Pending",
        error: undefined,
      };
    }),
  });

export const startQueueingAction =
  (ids: string[]): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (!ids.includes(file.id)) {
        return file;
      }

      if (!canBeQueued(file.status)) {
        return file;
      }

      return {
        ...file,
        status: "Queueing",
        runError: undefined,
        sourceId: undefined,
        workflowId: undefined,
        workflowTemplateId: undefined,
        workflowDestination: undefined,
      };
    }),
  });

export const setQueueResultAction =
  (
    id: string,
    status: "Queued" | "QueueFailed",
    sourceId?: string,
    runError?: string
  ): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id) {
        return file;
      }

      if (file.status !== "Queueing" && file.status !== "Queued") {
        return file;
      }

      if (status === "Queued") {
        if (!sourceId) {
          return {
            ...file,
            status: "QueueFailed",
            sourceId: undefined,
            runError: "Queued workflow is missing source id.",
          };
        }

        return {
          ...file,
          status: "Queued",
          sourceId,
          runError: undefined,
        };
      }

      return {
        ...file,
        status: "QueueFailed",
        sourceId: undefined,
        runError: runError ?? "Failed to queue workflow.",
      };
    }),
  });

export const markWorkflowStartedAction =
  (
    id: string,
    workflowId: string,
    templateId?: string,
    workflowType?: WorkflowType,
    workflowDestination?: string
  ): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id || file.status !== "Queued") {
        return file;
      }

      return {
        ...file,
        status: "WorkflowStarted",
        workflowId,
        workflowTemplateId: templateId,
        selectedWorkflowType: workflowType ?? file.selectedWorkflowType,
        runError: undefined,
        workflowDestination,
      };
    }),
  });

export const setWorkflowDestinationAction =
  (id: string, workflowDestination: string): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id || file.status !== "WorkflowStarted") {
        return file;
      }

      return {
        ...file,
        workflowDestination,
      };
    }),
  });

export const removeFileAction =
  (id: string): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.filter((file) => file.id !== id),
  });

// Analysis actions

export const startAnalysisAction =
  (ids: string[]): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (!ids.includes(file.id)) {
        return file;
      }

      if (file.status !== "Uploaded" || !file.catalogueId) {
        return file;
      }

      return {
        ...file,
        analysisStatus: "Analyzing" as const,
        matchDecision: undefined,
        analysisError: undefined,
      };
    }),
  });

export const setAnalysisResultAction =
  (
    id: string,
    matchDecision?: MatchDocumentsDecision,
    selectedWorkflowType?: WorkflowType
  ): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id || file.analysisStatus !== "Analyzing") {
        return file;
      }

      return {
        ...file,
        analysisStatus: "Analyzed" as const,
        matchDecision,
        selectedWorkflowType: selectedWorkflowType ?? file.selectedWorkflowType,
        analysisError: undefined,
      };
    }),
  });

export const setAnalysisFailedAction =
  (id: string, error: string): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id || file.analysisStatus !== "Analyzing") {
        return file;
      }

      return {
        ...file,
        analysisStatus: "Failed" as const,
        matchDecision: undefined,
        analysisError: error,
      };
    }),
  });

// Match-mode specific actions

export const selectWorkflowAction =
  (id: string, workflowType: WorkflowType): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id) {
        return file;
      }

      if (file.status !== "Uploaded" && file.status !== "QueueFailed") {
        return file;
      }

      return {
        ...file,
        selectedWorkflowType: workflowType,
        runError: undefined,
      };
    }),
  });

export const cancelAnalysisAction =
  (id: string): StateAction<UploadDocumentsState> =>
  (state) => ({
    ...state,
    files: state.files.map((file) => {
      if (file.id !== id) {
        return file;
      }

      return {
        ...file,
        analysisStatus: undefined,
        matchDecision: undefined,
        analysisError: undefined,
      };
    }),
  });

export const initialUploadDocumentsState: UploadDocumentsState = { files: [] };
